// A take played to a beat (the recorder flashes the tempo: see components/social/VideoRecorder.js),
// and where its beats are. The tempo is known, so this isn't beat tracking, only where that tempo's
// grid sits: slide it across the take's hits (heard as the drum hearing hears them,
// src/lib/audio-search/transcribe.js) and keep where they fall best. A hit counts most on a beat,
// less on an eighth (straight, or swung two thirds of the way). That measures where the player
// actually played, so the phone's delays (the microphone's, the recorder's) and a player sitting
// behind the flash all come out in it. The count-in says which beat starts bar 1: the one nearest
// where it said bar 1 would come. When the hits don't settle it (held notes, a few soft ones), the
// count-in alone places it. The same fit finds where a band made for the take keeps its beat.
import { hear } from "../audio-search/transcribe";

const STEP = 0.001, SPREAD = 0.025;
// The hearing places a hit about this long before its attack (its window takes the attack in
// first); measured on piano, drums and a band, 21–24 ms.
const ATTACK = 0.022;
// Where a hit may fall in a beat, and how much it counts there.
const PLACES = [[0, 1], [0.5, 0.5], [2 / 3, 0.4]];
// Phases nearly as good as the best (an off-beat grid, when every eighth is as loud) are left to
// the count-in; a grid is heard when its best phase stands well above the average one.
const NEARLY = 0.85, ABOVE = 1.6, LEAST = 0.3;

// How well hits fit a grid of beats `period` seconds apart, at each phase (in STEPs from 0): the
// share of their strength that falls on it.
export function phaseScores(onsets, period) {
  const steps = Math.max(1, Math.round(period / STEP)), scores = new Float64Array(steps), total = onsets.reduce((sum, onset) => sum + onset.strength, 0) || 1;
  const near = Float64Array.from({ length: steps }, (_, step) => {
    const x = step / steps;
    return Math.max(...PLACES.map(([place, weight]) => { const d = Math.min(Math.abs(x - place), 1 - Math.abs(x - place)) * period; return weight * Math.exp(-(d * d) / (2 * SPREAD * SPREAD)); }));
  });
  for (const { time, strength } of onsets) {
    const at = Math.round((((time / period) % 1) + 1) % 1 * steps);
    for (let step = 0; step < steps; step++) scores[step] += strength * near[(at - step + steps * 2) % steps];
  }
  return scores.map((score) => score / total);
}

// The phases (as seconds into a beat) to choose from: the best, and any nearly as good.
function candidates(scores, period) {
  const steps = scores.length, mean = scores.reduce((sum, value) => sum + value, 0) / steps;
  let best = 0;
  for (let step = 1; step < steps; step++) if (scores[step] > scores[best]) best = step;
  const peaks = [];
  for (let step = 0; step < steps; step++) {
    const value = scores[step];
    if (value >= scores[(step - 1 + steps) % steps] && value >= scores[(step + 1) % steps] && value >= NEARLY * scores[best]) peaks.push(step);
  }
  return { phases: peaks.map((step) => (step / steps) * period), fit: scores[best], heard: scores[best] > ABOVE * mean && scores[best] > LEAST };
}

// Where bar 1 starts in a take, given where the count-in said it would come (`near`, seconds into
// the recording): { downbeat, period, heard (whether the take's hits placed it, or only the
// count-in), fit (the share of their strength on the grid), swing (where its off-beat eighths fall
// in the beat: 0.5 straight, about 0.67 swung; null when there aren't enough) }.
export function findGrid(onsets, { tempo, near }) {
  const period = 60 / tempo, playing = onsets.filter((onset) => onset.time >= near - period / 2);
  if (playing.length < 4) return { downbeat: near, period, heard: false, fit: 0, swing: null };
  const { phases, fit, heard } = candidates(phaseScores(playing, period), period);
  if (!heard) return { downbeat: near, period, heard: false, fit, swing: null };
  const place = (phase) => phase + Math.round((near - phase) / period) * period;
  const downbeat = phases.map(place).reduce((a, b) => (Math.abs(b - near) < Math.abs(a - near) ? b : a));
  return { downbeat, period, heard: true, fit, swing: swingOf(playing, downbeat, period) };
}

// Where a take's off-beat eighths fall in the beat, by their strength: straight, or swung up to two
// thirds of the way (a sixteenth at three quarters isn't an eighth).
export function swingOf(onsets, downbeat, period) {
  let sum = 0, weight = 0, count = 0;
  for (const { time, strength } of onsets) {
    const x = ((((time - downbeat) / period) % 1) + 1) % 1;
    if (x > 0.42 && x < 0.71) { sum += x * strength; weight += strength; count++; }
  }
  return weight > 0 && count >= 3 ? sum / weight : null;
}

// Where a band made for a take keeps its beat: how far its grid sits from its start (which was the
// take's bar 1), in seconds; null when its hits don't say. A band built around the take starts in
// time with it, give or take the decoders' delays and the model's own, so only the best fit within
// `within` of its start counts: a swung band's loudest notes can sit a third of the way into the
// beat, and fit there better than on it.
export function bandOffset(onsets, { tempo, within = 0.08 }) {
  const period = 60 / tempo, scores = phaseScores(onsets, period), steps = scores.length, mean = scores.reduce((sum, value) => sum + value, 0) / steps;
  const reach = Math.min(Math.floor(steps / 2) - 1, Math.round(within / STEP)), at = (step) => scores[(step + steps) % steps];
  // The best peak inside the window (a stronger one just outside can lift its edge higher).
  let best = null;
  for (let step = -reach + 1; step < reach; step++) {
    if (at(step) >= at(step - 1) && at(step) >= at(step + 1) && at(step) > mean && at(step) > LEAST / 2 && (best == null || at(step) > at(best))) best = step;
  }
  return best == null ? null : best * STEP;
}

// The same, from samples (mono, any rate; the first 30 seconds are heard).
const heard = (samples, rate) => hear(samples, rate).map((onset) => ({ time: onset.time + ATTACK, strength: onset.strength }));
export const takeGrid = (samples, rate, options) => findGrid(heard(samples, rate), options);
export const bandShift = (samples, rate, options) => bandOffset(heard(samples, rate), options);
